import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo, Wordmark } from '@/components/ui/Logo';
import { LanguageSwitch } from '@/components/ui/LanguageSwitch';

/**
 * The chrome every public page shares.
 *
 * Extracted from `LandingPage` when the second public page arrived, rather than
 * copied — four pages with four slightly different headers is how a marketing
 * site starts looking like four different products.
 *
 * Each of these has a pre-rendered HTML shell with its own title and
 * description (`tools/prerender.mjs`), because no crawler runs JavaScript and an
 * SPA would otherwise give every page the same one.
 */
export function MarketingPage({
  title,
  lede,
  children,
}: {
  title: string;
  lede?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <header className="border-b border-line">
        <div className="gutter mx-auto flex w-full max-w-[1100px] items-center gap-3 py-4">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={32} tone="leaf" />
            <Wordmark className="text-leaf" />
          </Link>
          <span className="ml-auto flex items-center gap-4">
            <LanguageSwitch />
            <Link
              to="/signin"
              className="min-h-tap text-body text-leaf-soft underline-offset-4 hover:underline"
            >
              {t('landing.haveAccount')}
            </Link>
          </span>
        </div>
      </header>

      <main className="gutter mx-auto w-full max-w-[720px] flex-1 py-16">
        <h1 className="font-display text-[40px] font-light leading-[1.1] md:text-[52px]">
          {title}
        </h1>
        {lede && <p className="mt-5 max-w-prose text-body text-leaf-soft">{lede}</p>}
        <div className="mt-12 flex flex-col gap-12">{children}</div>
      </main>

      <MarketingFooter />
    </div>
  );
}

export function MarketingFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-line">
      <div className="gutter mx-auto flex w-full max-w-[1100px] flex-wrap items-center gap-x-6 gap-y-3 py-8 text-caption text-leaf-soft">
        <span>© {new Date().getFullYear()} Guabi</span>
        <Link to="/how-it-works" className="underline-offset-4 hover:underline">
          {t('marketing.howItWorks')}
        </Link>
        <Link to="/privacy" className="underline-offset-4 hover:underline">
          {t('marketing.privacy')}
        </Link>
        <span className="ml-auto">{t('marketing.promise')}</span>
      </div>
    </footer>
  );
}

/** A heading and prose. The only section shape these pages need. */
export function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-8 first:border-0 first:pt-0">
      <h2 className="font-display text-titleSm">{heading}</h2>
      <div className="flex max-w-prose flex-col gap-3 text-body leading-relaxed text-leaf-soft">
        {children}
      </div>
    </section>
  );
}
